<!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
<template>
  <div class="mmm-search-flyout">
    <button
      ref="button"
      @click="toggleFlyout"
      @keydown.enter.prevent="toggleFlyout"
      :aria-expanded="isOpen"
      aria-haspopup="dialog"
      aria-controls="flyout"
      aria-label="Suchmodal"
      class="mmm-search-flyout__trigger"
      :class="{ 'mmm-search-flyout__trigger--open': isOpen }"
    >
      <IconSearch />
    </button>

    <dialog
      v-if="isOpen"
      ref="dialog"
      id="flyout"
      aria-modal="true"
      aria-labelledby="dialog-title"
      @click.self="closeFlyout"
      @keydown.self="closeFlyout"
      class="mmm-search-flyout__dialog mmm-search-flyout-dialog"
    >
      <form @submit.prevent="submit">
        <label for="input"
          ><span class="mmm-search-flyout-dialog__label">Suchbegriff</span>
          <input
            id="input"
            ref="input"
            placeholder="Suchbegriff"
            v-model="inputValue"
            @keydown.enter.prevent="submit"
            type="text"
            class="mmm-search-flyout-dialog__input"
        /></label>
        <button
          type="submit"
          class="mmm-search-flyout-dialog__submit"
          aria-label="Suche abschicken"
        >
          <IconSearch />
        </button>
      </form>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

import IconSearch from '~icons/mmm/search.svg';

const isOpen = ref(false);
const dialog = ref<HTMLDialogElement | null>(null);
const input = ref<HTMLInputElement | null>(null);
const button = ref<HTMLButtonElement | null>(null);
const inputValue = ref('');

const emit = defineEmits(['onOpen', 'onClose']);

function toggleFlyout() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    nextTick(() => {
      dialog.value?.showModal();
      positionFlyout();
      input.value?.focus();
    });
  } else {
    closeFlyout();
  }

  if (isOpen.value) {
    emit('onOpen');
  }
}

function closeFlyout() {
  isOpen.value = false;
  dialog.value?.close();
  nextTick(() => {
    button.value?.focus();
    emit('onClose');
  });
}

function submit() {
  closeFlyout();
  window.location.replace(`/s/enterprise-search-result/?query=${input.value!.value}`);
}

function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isOpen.value) {
    e.preventDefault();
    closeFlyout();
  }
}

function positionFlyout() {
  const trigger = button.value;
  const dlg = dialog.value;
  if (!trigger || !dlg) return;

  const rect = trigger.getBoundingClientRect();
  const rectDlg = dlg.getBoundingClientRect();

  // 2 and 5 are adjustments so it fits perfectly over the trigger
  dlg.style.top = `${rect.top - 2}px`;
  dlg.style.left = `${rect.left - rectDlg.width + rect.width + 5}px`;
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeyDown);
});
</script>

<style scoped lang="scss">
.mmm-search-flyout {
  &__trigger {
    height: 40px;
    width: 40px;
    background-color: transparent;
    border: none;
    cursor: pointer;
  }

  &__dialog {
    &::backdrop {
      background: rgba(0, 0, 0, 0);
    }
  }
}

.mmm-search-flyout-dialog {
  top: 0;
  height: 40px;
  box-sizing: content-box;
  padding: 0;
  margin: 0;
  left: auto;
  position: absolute;
  display: flex;
  align-items: center;
  gap: 6px;
  overflow: hidden;

  &__input {
    height: 40px;
    margin: 0;
    width: 300px;
    border: none;
    padding: 0 52px 0 12px;
    transform: translate(6px, -2px);
    font: inherit;
    border-width: 1px;
    outline: 3px solid #ccc;
    box-sizing: border-box;
    outline-offset: -1px;
  }

  &__submit {
    position: relative;
    height: 40px;
    margin-left: -40px;
    top: 5px;
    background: none;
    border: none;
    font-size: 16px;
    cursor: pointer;
  }

  &__label {
    height: 0;
    width: 0;
    opacity: 0;
    pointer-events: none;
    position: absolute;
  }
}
</style>
